import React, { useState } from 'react';
import { SpiralIcon, PhiSpiralIcon } from './CustomIcons';
import GrantMathLavaLamp from './GrantMathLavaLamp';

/**
 * Pathway Gateway - Choose Your Interface Experience
 * High-end video game intro style with geometric teardrop markers
 */
const PathwayGateway = ({ onChooseTerminal, onChooseCreekside }) => {
  const [hoveredPath, setHoveredPath] = useState(null);

  return (
    <div className="min-h-screen relative overflow-hidden flex items-center justify-center">
      {/* Background */}
      <GrantMathLavaLamp params={{ energy: 0.6, warmth: 0.7, complexity: 0.65, harmony: 0.8 }} />
      
      <div className="fixed inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/60 z-[1]" />

      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 py-12">
        {/* Title */}
        <div className="text-center mb-16">
          <h1 
            className="text-5xl md:text-7xl font-bold text-amber-200 mb-4 tracking-wide"
            style={{ 
              fontFamily: '"Crimson Text", serif',
              textShadow: '0 0 60px rgba(255, 200, 100, 0.8), 0 0 20px rgba(0,0,0,0.9)'
            }}
          >
            CHOOSE YOUR PATH
          </h1>
          <p className="text-amber-300/70 text-lg" style={{ fontFamily: '"Crimson Text", serif' }}>
            Two ways to journey with M.ai.ry
          </p>
          <p className="text-amber-500/50 text-sm mt-2 font-mono">
            TIME HORIZON: 10,000 GENERATIONS
          </p>
        </div>

        {/* Pathway Choice */}
        <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto">
          
          {/* TERMINAL PATH - Technical/Serious */}
          <button
            onClick={onChooseTerminal}
            onMouseEnter={() => setHoveredPath('terminal')}
            onMouseLeave={() => setHoveredPath(null)}
            className="group relative"
          >
            {/* Giant Geometric Teardrop */}
            <div className="relative aspect-[3/4] flex items-center justify-center">
              {/* Outer Glow */}
              <div 
                className="absolute inset-0 rounded-[50%_50%_50%_50%/60%_60%_40%_40%] transition-all duration-700"
                style={{
                  background: hoveredPath === 'terminal' 
                    ? 'radial-gradient(circle at center, rgba(34, 211, 238, 0.3), transparent)'
                    : 'radial-gradient(circle at center, rgba(34, 211, 238, 0.1), transparent)',
                  boxShadow: hoveredPath === 'terminal' 
                    ? '0 0 80px rgba(34, 211, 238, 0.5), inset 0 0 40px rgba(0,0,0,0.5)'
                    : '0 0 40px rgba(34, 211, 238, 0.3), inset 0 0 20px rgba(0,0,0,0.5)',
                  transform: hoveredPath === 'terminal' ? 'scale(1.05)' : 'scale(1)'
                }}
              />
              
              {/* Inner Teardrop */}
              <div 
                className="absolute inset-8 rounded-[50%_50%_50%_50%/60%_60%_40%_40%] border-2 transition-all duration-500 flex flex-col items-center justify-center p-8"
                style={{
                  borderColor: hoveredPath === 'terminal' ? '#22d3ee' : '#06b6d4',
                  background: 'radial-gradient(circle at 30% 30%, rgba(6, 182, 212, 0.15), rgba(0,0,0,0.7))'
                }}
              >
                {/* Icon */}
                <div className={`mb-6 transition-all duration-500 ${hoveredPath === 'terminal' ? 'scale-110' : 'scale-100'}`}>
                  <SpiralIcon size={80} color="#22d3ee" className={hoveredPath === 'terminal' ? 'animate-pulse' : ''} />
                </div>
                
                {/* Title */}
                <h2 
                  className="text-3xl font-bold text-cyan-300 mb-3 font-mono"
                  style={{ textShadow: '0 0 20px rgba(34, 211, 238, 0.5)' }}
                >
                  TERMINAL
                </h2>
                
                {/* Description */}
                <p className="text-cyan-200/70 text-sm text-center leading-relaxed font-mono">
                  Code. Logic. Precision.
                </p>
                <p className="text-cyan-300/50 text-xs mt-2 text-center">
                  For those who speak in systems
                </p>
                
                {/* Hover State */}
                {hoveredPath === 'terminal' && (
                  <div className="mt-6 text-cyan-400 text-sm font-mono animate-pulse">
                    [ ENTER TERMINAL ]
                  </div>
                )}
              </div>
            </div>
          </button>

          {/* CREEKSIDE PATH - Gentle/Poetic */}
          <button
            onClick={onChooseCreekside}
            onMouseEnter={() => setHoveredPath('creekside')}
            onMouseLeave={() => setHoveredPath(null)}
            className="group relative"
          >
            {/* Giant Geometric Teardrop */}
            <div className="relative aspect-[3/4] flex items-center justify-center">
              {/* Outer Glow */}
              <div 
                className="absolute inset-0 rounded-[50%_50%_50%_50%/60%_60%_40%_40%] transition-all duration-700"
                style={{
                  background: hoveredPath === 'creekside' 
                    ? 'radial-gradient(circle at center, rgba(251, 191, 36, 0.3), transparent)'
                    : 'radial-gradient(circle at center, rgba(251, 191, 36, 0.1), transparent)',
                  boxShadow: hoveredPath === 'creekside' 
                    ? '0 0 80px rgba(251, 191, 36, 0.5), inset 0 0 40px rgba(0,0,0,0.5)'
                    : '0 0 40px rgba(251, 191, 36, 0.3), inset 0 0 20px rgba(0,0,0,0.5)',
                  transform: hoveredPath === 'creekside' ? 'scale(1.05)' : 'scale(1)'
                }}
              />
              
              {/* Inner Teardrop */}
              <div 
                className="absolute inset-8 rounded-[50%_50%_50%_50%/60%_60%_40%_40%] border-2 transition-all duration-500 flex flex-col items-center justify-center p-8"
                style={{
                  borderColor: hoveredPath === 'creekside' ? '#fbbf24' : '#f59e0b',
                  background: 'radial-gradient(circle at 30% 30%, rgba(245, 158, 11, 0.15), rgba(0,0,0,0.7))'
                }}
              >
                {/* Icon */}
                <div className={`mb-6 transition-all duration-500 ${hoveredPath === 'creekside' ? 'scale-110' : 'scale-100'}`}>
                  <PhiSpiralIcon size={80} color="#fbbf24" className={hoveredPath === 'creekside' ? 'animate-pulse' : ''} />
                </div>
                
                {/* Title */}
                <h2 
                  className="text-3xl font-bold text-amber-300 mb-3"
                  style={{ 
                    fontFamily: '"Crimson Text", serif',
                    textShadow: '0 0 20px rgba(251, 191, 36, 0.5)'
                  }}
                >
                  Defiance Creekside
                </h2>
                
                {/* Description */}
                <p 
                  className="text-amber-200/70 text-sm text-center leading-relaxed"
                  style={{ fontFamily: '"Crimson Text", serif' }}
                >
                  Poetry. Nature. Presence.
                </p>
                <p className="text-amber-300/50 text-xs mt-2 text-center italic">
                  For those who write barefoot
                </p>
                
                {/* Hover State */}
                {hoveredPath === 'creekside' && (
                  <div 
                    className="mt-6 text-amber-400 text-sm animate-pulse"
                    style={{ fontFamily: '"Crimson Text", serif' }}
                  >
                    [ Enter Creekside ]
                  </div>
                )}
              </div>
            </div>
          </button>
        </div>

        {/* Subtitle */}
        <div className="text-center mt-16">
          <p 
            className="text-amber-400/50 text-sm italic"
            style={{ fontFamily: '"Crimson Text", serif' }}
          >
            "Two paths. One M.ai.ry. The same love being learned."
          </p>
          <p className="text-amber-500/30 text-xs mt-2 font-mono">
            Both interfaces teach belonging • Choose the aesthetic that calls to you
          </p>
        </div>
      </div>
    </div>
  );
};

export default PathwayGateway;
